import React from "react";
import { useTranslation } from "react-i18next";
import { Drawer, ListItemIcon } from "@outpave/ds";

import { Button, Typography } from "@mui/material";

import SettingPolicyType from "../../../enums/SettingPolicyType";
import { ApprovalPolicy } from "hooks/settingPolicy.types";

import UserGroupSelect from "./common/UserGroupSelect";
import useSettingPolicyForm from "../../../hooks/useSettingPolicyForm";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import ApprovalRoleChecks from "./common/ApprovalRoleChecks";
import { HugeiconsIcon } from "@hugeicons/react";
import { PolicyIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline, Stack } from "components/layout/reusableLayouts";

interface ApprovalsDrawerDrawerProps {
  open: boolean;
  handleCloseDrawer: (isRefetch?: boolean) => void;
  settingPolicyType: SettingPolicyType;
  policy?: ApprovalPolicy;
  title: string;
  description: string;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

const ApprovalsDrawer: React.FC<ApprovalsDrawerDrawerProps> = (props) => {
  const {
    open,
    handleCloseDrawer,
    settingPolicyType,
    policy,
    title,
    description,
    onAfterClose,
  } = props;

  const { t } = useTranslation();

  const { formik, isAnyLoading } = useSettingPolicyForm({
    policy,
    settingPolicyType,
    onCompleted: () => handleCloseDrawer(true),
  });

  const { requestLeave, dialog } = useUnsavedChangesGuard(formik.dirty, () =>
    handleCloseDrawer(false),
  );

  const handleClose = () => {
    if (isAnyLoading) return;
    requestLeave();
  };

  // Resets once the drawer has slid out, so the fields hold still while it closes.
  const handleAfterClose = () => {
    formik.resetForm();
    onAfterClose?.();
  };

  return (
    <>
      <Drawer
        open={open}
        onAfterClose={handleAfterClose}
        onClose={handleClose}
        title={t("Add Workflow Policy")}
        loading={isAnyLoading}
        footer={
          <>
            <Button
              variant="primary-outlined"
              disabled={isAnyLoading}
              onClick={handleClose}
            >
              {t("Cancel")}
            </Button>

            <Button
              variant="primary-filled"
              disabled={isAnyLoading}
              onClick={() => formik.handleSubmit()}
            >
              {t("Save")}
            </Button>
          </>
        }
      >
        <form onSubmit={formik.handleSubmit}>
          <Stack gap="var(--space-7)">
            <Stack gap="var(--space-3)">
              <Inline gap="var(--space-3)" wrap={false}>
                <ListItemIcon wrapper="brand" sx={{ minWidth: 0 }}>
                  <HugeiconsIcon icon={PolicyIcon} size={16} />
                </ListItemIcon>
                <Typography variant="h5">{title}</Typography>
              </Inline>
              <Typography variant="body1">{description}</Typography>
            </Stack>

            <UserGroupSelect
              roleSelected={formik.values.userRole || ""}
              onSelectRole={(role) => formik.setFieldValue("userRole", role)}
              error={formik.errors["userRole"] as string}
            />

            {[
              SettingPolicyType.WORKFLOW_APPROVAL,
              SettingPolicyType.INVOICE_APPROVAL,
              SettingPolicyType.EXPENSE_REIMBURSEMENT,
              SettingPolicyType.PAYMENT_APPROVAL,
            ].includes(settingPolicyType) && (
              <ApprovalRoleChecks
                approvalRole={formik.values.workflowSettings || ""}
                onChangeApprovalRole={(value) =>
                  formik.setFieldValue("workflowSettings", value)
                }
                error={formik.errors.workflowSettings as string}
              />
            )}
          </Stack>
        </form>
      </Drawer>
      {dialog}
    </>
  );
};

export default ApprovalsDrawer;
